import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import axiosInstance from '../api/axiosConfig';
import { connectSocket, onSocketReconnect } from './socketClient';

/**
 * System-wide announcement popup. Two ways it appears:
 *   1. Live — a socket 'announcement:new' broadcast while this tab is open.
 *   2. Catch-up — GET /api/announcements/active on mount, so a reload or a
 *      fresh login DURING the display window still shows it.
 *
 * The popup itself (AnnouncementModal) owns the countdown and the "can't close
 * until the timer runs out" rule. This hook only decides WHICH announcement is
 * current; a `dismissedIdRef` stops the same one (arriving on both channels, or
 * re-fetched after a route change) from re-opening once it has been closed.
 */
export function useAnnouncements() {
    const { user, token } = useAuth();
    const [announcement, setAnnouncement] = useState(null);
    const dismissedIdRef = useRef(null);

    const show = useCallback((payload) => {
        if (!payload?.id || payload.id === dismissedIdRef.current) return;
        setAnnouncement(payload);
    }, []);

    const dismiss = useCallback((id) => {
        dismissedIdRef.current = id;
        setAnnouncement((cur) => (cur && cur.id === id ? null : cur));
    }, []);

    // Catch-up fetch on mount / login — pulled into its own function so the
    // reconnect effect below can reuse the exact same request instead of a
    // parallel implementation.
    const fetchActive = useCallback(() => {
        axiosInstance.get('/api/announcements/active')
            .then(({ data }) => show(data?.data))
            .catch((err) => console.error('[announcements] active fetch failed:', err.message));
    }, [show]);

    useEffect(() => {
        if (!user || !token) return undefined;
        let cancelled = false;
        axiosInstance.get('/api/announcements/active')
            .then(({ data }) => { if (!cancelled) show(data?.data); })
            .catch((err) => console.error('[announcements] active fetch failed:', err.message));
        return () => { cancelled = true; };
    }, [user, token, show]);

    // Recovery: the live 'announcement:new' broadcast can be missed the same
    // way any socket event can (offline, tab closed) — a reconnect re-runs
    // the SAME catch-up request above, so a popup that started while this
    // tab was disconnected still appears once it's back. `show()` already
    // no-ops against `dismissedIdRef`, so this cannot re-open one the user
    // already closed.
    useEffect(() => {
        if (!user || !token) return undefined;
        return onSocketReconnect(fetchActive);
    }, [user, token, fetchActive]);

    // Live channel.
    useEffect(() => {
        if (!user || !token) return undefined;
        const socket = connectSocket(token);
        if (!socket) return undefined;

        const onNew = (payload) => {
            dismissedIdRef.current = null; // a brand-new announcement is never pre-dismissed
            setAnnouncement(payload);
        };
        const onCleared = ({ id }) => {
            setAnnouncement((cur) => (cur && cur.id === id ? null : cur));
        };

        socket.on('announcement:new', onNew);
        socket.on('announcement:cleared', onCleared);
        return () => {
            socket.off('announcement:new', onNew);
            socket.off('announcement:cleared', onCleared);
        };
    }, [user, token]);

    return { announcement, dismiss };
}
